import type { i18n as I18nInstance } from 'i18next';

import availableLanguages from '@/components/common/availableLanguages.json';

export const DEFAULT_LANGUAGE = 'en';
export const LANGUAGE_QUERY_PARAMETER = 'lang';
export const LEGACY_LANGUAGE_QUERY_PARAMETER = 'l';
export const LANGUAGE_LOCAL_STORAGE_KEY = 'language';

export const LANGUAGE_QUERY_DETECTOR = 'languageQuery';
export const LANGUAGE_DETECTION_ORDER = [LANGUAGE_QUERY_DETECTOR, 'localStorage', 'navigator'] as const;

const supportedLanguages = new Set(availableLanguages.map((language) => language.code));
const LANGUAGE_CODE_ALIASES: Record<string, string> = {
  cs: 'cz',
};

export function getQueryLanguage(search: string): string | null {
  const params = new URLSearchParams(search);
  return params.get(LANGUAGE_QUERY_PARAMETER) ?? params.get(LEGACY_LANGUAGE_QUERY_PARAMETER);
}

export function normalizeLanguage(language: string | undefined | null): string | undefined {
  if (!language) {
    return undefined;
  }

  let canonicalLanguage: string | undefined;
  try {
    [canonicalLanguage] = Intl.getCanonicalLocales(language);
  } catch {
    return undefined;
  }

  const languageCode = canonicalLanguage?.split('-')[0].toLowerCase();
  const normalizedLanguageCode = languageCode ? (LANGUAGE_CODE_ALIASES[languageCode] ?? languageCode) : undefined;
  return normalizedLanguageCode && supportedLanguages.has(normalizedLanguageCode) ? normalizedLanguageCode : undefined;
}

export function normalizeDetectedLanguage(language: string): string {
  return normalizeLanguage(language) ?? '__invalid__';
}

export function resolveLanguage(
  queryLanguage: string | undefined | null,
  localStorageLanguage: string | undefined | null,
  browserLanguage: string | undefined | null
): string {
  return (
    normalizeLanguage(queryLanguage) ??
    normalizeLanguage(localStorageLanguage) ??
    normalizeLanguage(browserLanguage) ??
    DEFAULT_LANGUAGE
  );
}

export function getCurrentLanguage(i18nInstance: I18nInstance): string {
  if (i18nInstance.isInitialized) {
    return normalizeLanguage(i18nInstance.resolvedLanguage ?? i18nInstance.language) ?? DEFAULT_LANGUAGE;
  }

  const queryLanguage = getQueryLanguage(window.location.search);
  const localStorageLanguage = localStorage.getItem(LANGUAGE_LOCAL_STORAGE_KEY);
  const browserLanguage = navigator.language;

  return resolveLanguage(queryLanguage, localStorageLanguage, browserLanguage);
}
